import { Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { SwiperOptions } from 'swiper';
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from '../../../app.config';
import { CsIconModule } from '@components/icon';
import { SwiperModule } from 'swiper/angular';
import { ExploreSlide, FeedItem, MediaItem, MediaLanguageJson, SLIDE_HEIGHT_PX, MAX_FEEDS } from './explore-slider-banner.constants';

@Component({
  selector: 'app-explore-slider-banner',
  templateUrl: './explore-slider-banner.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [`
    app-explore-slider-banner .swiper-slide {
      width: auto !important;
    }
  `],
  imports: [
    CsIconModule,
    SwiperModule,
  ]
})
export class ExploreSliderBannerComponent {
  private readonly conn = inject(ConnectionService);
  protected readonly appConfig = inject(AppConfig);

  readonly slides = signal<ExploreSlide[]>([]);
  readonly SLIDE_HEIGHT = SLIDE_HEIGHT_PX;

  readonly swiperConfig: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 12,
    navigation: { prevEl: 'nonext', nextEl: 'nonext' },
  };

  async ngOnInit(): Promise<void> {
    try {
      const rawFeeds = await this.conn.fetchSuitableFeedsForUser();
      const feeds = (rawFeeds?.data ?? []) as FeedItem[];
      const user = this.conn.getActingUser();
      const lang = user?.get('languagePreference') || this.appConfig.Shared.Languages.EN;

      this.slides.set(
        feeds
          .slice(0, MAX_FEEDS)
          .map((feedItem: FeedItem) => this.transformToSlide(feedItem, lang))
          .filter((slide: ExploreSlide | null): slide is ExploreSlide => !!slide)
      );
    } catch {
      this.slides.set([]);
    }
  }

  navigateToPost(objectId: string): void {
    this.conn.navigateToURL(`/user?tab=explore&id=${objectId}`);
  }

  navigateToExplore(): void {
    this.conn.navigateToURL('/user?tab=explore');
  }

  private transformToSlide(feedItem: FeedItem, language: string): ExploreSlide | null {
    const mediaArray = this.extractMediaForLanguage(feedItem.mediaLanguageJson, language);
    if (!mediaArray.length) return null;

    const firstMedia = mediaArray[0];
    const isVideo = firstMedia.type === this.appConfig.Shared.Feed.VIDEO
      || feedItem.type === this.appConfig.Shared.Feed.VIDEO;

    const imageUrl = isVideo ? (firstMedia.thumbnailUrl || firstMedia.url) : firstMedia.url;

    if (!imageUrl) return null;

    return {
      objectId: feedItem.objectId,
      type: feedItem.type || this.appConfig.Shared.Feed.IMAGE,
      imageUrl,
      aspectRatio: firstMedia.aspectRatio || '1:1',
      slideWidth: this.computeWidth(firstMedia.aspectRatio),
    };
  }

  private extractMediaForLanguage(mediaLanguageJson: MediaLanguageJson | undefined, language: string): MediaItem[] {
    if (!mediaLanguageJson) return [];
    const mediaArray = mediaLanguageJson[language]
      || mediaLanguageJson.en
      || Object.values(mediaLanguageJson)[0]
      || [];
    return Array.isArray(mediaArray) ? mediaArray : [];
  }

  private computeWidth(aspectRatio: string | undefined): number {
    if (!aspectRatio || typeof aspectRatio !== 'string') return SLIDE_HEIGHT_PX;
    const parts = aspectRatio.split(':');
    const w = Number(parts[0]);
    const h = Number(parts[1]);
    if (!w || !h) return SLIDE_HEIGHT_PX;
    return Math.round(SLIDE_HEIGHT_PX * (w / h));
  }
}
